'use client'

import { useId, useState } from 'react'
import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye'
import { EyeSlashIcon } from '@phosphor-icons/react/dist/ssr/EyeSlash'
import { InfoIcon } from '@phosphor-icons/react/dist/ssr/Info'
import { cx } from 'class-variance-authority'

import { Input } from '../Input/input.atom'

type PasswordCondition = {
  regex: RegExp
  description: string
}

type NativeInputPasswordProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange'
>

export type InputPasswordProps = NativeInputPasswordProps & {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  value?: React.InputHTMLAttributes<HTMLInputElement>['value']
  defaultValue?: React.InputHTMLAttributes<HTMLInputElement>['defaultValue']
  variant?: string
  showPasswordStrength?: boolean
}

const passwordConditionsRegex: PasswordCondition[] = [
  { regex: /.{14,}/, description: '14 caractères' },
  { regex: /[a-z]/, description: 'Au moins une lettre minuscule' },
  { regex: /[A-Z]/, description: 'Au moins une lettre majuscule' },
  { regex: /\d/, description: 'Un chiffre' },
  { regex: /[@&!%]/, description: 'Caractère spécial (@,&,!,%)' },
]

const bgColors = [
  'from-red-500 to-red-400',
  'from-orange-500 to-orange-400',
  'from-orange-400 to-orange-300',
  'from-green-200 to-green-300',
  'from-green-300 to-green-400',
]
const textColors = [
  'text-red-500',
  'text-orange-500',
  'text-orange-400',
  'text-green-400',
  'text-green-500',
]
const strengthText = ['Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort']

export const InputPassword: React.FC<InputPasswordProps> = ({
  id,
  name,
  onChange,
  onBlur,
  value,
  defaultValue,
  variant,
  showPasswordStrength,
  ...rest
}) => {
  const generatedId = useId()
  const inputId = id ?? name ?? generatedId
  const normalizedDefaultValue = Array.isArray(defaultValue)
    ? defaultValue.join('')
    : defaultValue == null
      ? ''
      : String(defaultValue)
  const [password, setPassword] = useState(normalizedDefaultValue)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordConditions, setPasswordConditions] = useState(false)
  const passwordConditionsId = `${inputId}-password-conditions`

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (value === undefined) {
      setPassword(e.target.value)
    }
    onChange?.(e)
  }

  const normalizedValue = Array.isArray(value)
    ? value.join('')
    : value == null
      ? undefined
      : String(value)
  const passwordValue = normalizedValue ?? password
  const strengthScore = passwordConditionsRegex.filter(({ regex }) =>
    regex.test(passwordValue),
  ).length
  const passwordConditionsLabel = passwordConditions
    ? 'Masquer les conditions du mot de passe'
    : 'Afficher les conditions du mot de passe'

  return (
    <>
      <div className='relative'>
        <Input
          {...rest}
          type={showPassword ? 'text' : 'password'}
          id={inputId}
          name={name ?? inputId}
          onChange={handleChange}
          onBlur={onBlur}
          value={passwordValue}
          variant={variant as React.ComponentProps<typeof Input>['variant']}
        />
        <button
          type='button'
          onClick={() => setShowPassword(!showPassword)}
          className='absolute right-2 top-2.5'
          aria-label={
            showPassword
              ? 'Masquer le mot de passe'
              : 'Afficher le mot de passe'
          }
          aria-pressed={showPassword}
        >
          {showPassword ? (
            <EyeSlashIcon
              className='size-5 text-slate-400 hover:text-slate-300'
              aria-hidden='true'
            />
          ) : (
            <EyeIcon
              className='size-5 text-slate-400 hover:text-slate-300'
              aria-hidden='true'
            />
          )}
        </button>
      </div>
      {showPasswordStrength && (
        <>
          <div className='mt-2 flex items-center gap-2'>
            <PasswordStrength score={strengthScore} />
            <div className='flex shrink-0 items-center gap-1'>
              {strengthScore >= 1 && (
                <span
                  className={cx(
                    'whitespace-nowrap text-sm',
                    textColors[strengthScore - 1],
                  )}
                >
                  {strengthText[strengthScore - 1]}
                </span>
              )}
              <button
                type='button'
                title={passwordConditionsLabel}
                aria-label={passwordConditionsLabel}
                aria-expanded={passwordConditions}
                aria-controls={passwordConditionsId}
                onClick={() => setPasswordConditions(!passwordConditions)}
                className='inline-flex items-center justify-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'
              >
                <InfoIcon
                  className='size-6 text-neutral-500 cursor-pointer hover:opacity-40'
                  aria-hidden='true'
                />
              </button>
            </div>
          </div>
          {passwordConditions && (
            <PasswordConditions
              id={passwordConditionsId}
              password={passwordValue}
            />
          )}
        </>
      )}
    </>
  )
}

const PasswordStrength = ({ score }) => {
  const segmentWidth = 100 / 5

  const segments = Array.from({ length: 5 }, (_, index) => {
    if (index < score) {
      return segmentWidth
    }
    return 0
  })

  return (
    <div
      className='h-2 min-w-0 flex-1 bg-gray-100 rounded-full overflow-hidden'
      role='progressbar'
      aria-label='Force du mot de passe'
      aria-valuemin={0}
      aria-valuemax={5}
      aria-valuenow={score}
    >
      {segments.map((width, index) => (
        <div
          key={index}
          className={cx(
            'h-full bg-gradient-to-r',
            bgColors[Math.min(index, bgColors.length - 1)],
          )}
          style={{
            width: `${width}%`,
            transition: 'width 0.3s ease-in-out',
            float: 'left',
          }}
        />
      ))}
    </div>
  )
}

const PasswordConditions: React.FC<{ id?: string; password: string }> = ({
  id,
  password,
}) => (
  <div
    id={id}
    className='mt-1.5 w-full bg-neutral-100 rounded-sm p-2'
    role='region'
    aria-label='Conditions du mot de passe'
  >
    {passwordConditionsRegex.map(({ regex, description }) => {
      const isMet = regex.test(password)
      return (
        <div
          key={description}
          className={cx(
            'flex items-center text-sm',
            isMet ? 'text-green-500' : 'text-red-500',
          )}
        >
          {isMet ? (
            <CheckCircleIcon
              className='mr-1 size-5'
              weight='fill'
              aria-hidden='true'
            />
          ) : (
            <CheckCircleIcon className='mr-1 size-5' aria-hidden='true' />
          )}
          {description}
        </div>
      )
    })}
  </div>
)
